// src/components/sourcex/VersionHistoryModal.jsx
//
// SourceX template editor — browse, export, compare and restore prior versions
// of ONE VISIT.
//
// A version is snapshotted automatically (see templateSchema.js's
// makeTemplateVersion and templateVersionService.js's pushTemplateVersion,
// which writes it to the template's own `versions` subcollection) at the
// moments content could otherwise be silently lost:
//   - "built"    — right before an AI/protocol rebuild overwrites the
//                   current draft (GenerateTemplateModal.js)
//   - "approved" — every time a template is approved or re-approved
//                   (SectionEditor.js's markApproved), one per visit
//   - "backup"   — right before this modal's own Restore overwrites the
//                   current content, so restoring is itself never a one-way
//                   trip either
//
// Scoped to a visit, because a visit is what a coordinator works on and what
// exports as a document. Whole-template snapshots taken before that was true
// are still listed, in their own group, with the warning they need: restoring
// one rewrites every visit, not just this one.
//
// Export is the primary action deliberately. "What did this look like?" is
// asked far more often than "put it back", and answering it by restoring —
// then restoring again to undo — is a destructive way to read.

import { History, ArchiveRestore, FileDown, GitCompare, X, Loader2, AlertTriangle } from "lucide-react";
import { versionEventId } from "../../services/templateSchema";
import { approvalNumberOf, versionLabel } from "../../services/templateRedline";
import { describeActor } from "../../services/approverIdentity";

const KIND_LABEL = {
  built: "Built",
  approved: "Approved",
  backup: "Backup",
};
const KIND_COLOR = {
  built: "text-rose-200 bg-rose-400/15 border-rose-400/30",
  approved: "text-emerald-200 bg-emerald-400/15 border-emerald-400/30",
  backup: "text-slate-300 bg-white/10 border-white/20",
};

/** Fields in a whole-template snapshot, for the legacy group's summary. */
const countContent = (sections = []) => {
  let events = 0;
  let questions = 0;
  sections.forEach((s) => {
    (s.events || []).forEach((ev) => {
      events += 1;
      questions += (ev.questions || []).length;
    });
  });
  return { sections: sections.length, events, questions };
};

/** Fields in a visit-scoped snapshot. */
const countVisit = (sections = []) => {
  let questions = 0;
  sections.forEach((s) =>
    (s.events || []).forEach((ev) => {
      questions += (ev.questions || []).length;
    })
  );
  return questions;
};

const formatWhen = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso || "";
  return d.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

const actionBtn =
  "shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition disabled:opacity-40 disabled:cursor-not-allowed";

/**
 * @param {Array} versions  every version for this template, newest first
 * @param {string} eventId  the visit currently selected in the editor
 * @param {string} eventName
 * @param {(version: object) => void} onRestore
 * @param {(version: object) => void} [onExport]   download this version as .docx
 * @param {(version: object) => void} [onRedline]  show what changed since it
 * @param {string} [restoringId]  the version currently being restored
 * @param {string} [exportingId]  the version currently being exported
 * @param {boolean} [loading]
 * @param {() => void} onClose
 */
export default function VersionHistoryModal({
  versions,
  eventId = "",
  eventName = "",
  onRestore,
  onExport,
  onRedline,
  restoringId,
  exportingId,
  loading = false,
  onClose,
}) {
  const all = Array.isArray(versions) ? versions : [];
  // This visit's own history, and the whole-template snapshots. Anything
  // belonging to a DIFFERENT visit is not shown at all — it isn't this visit's
  // timeline.
  //
  // A caller with no visit selected (the mapping page) therefore sees only the
  // un-scoped group, which is exactly the step-3 assemblies: each one is the
  // whole set of built documents as it stood before a rebuild replaced it.
  const mine = all.filter((v) => versionEventId(v) === eventId && eventId);
  const legacy = all.filter((v) => !versionEventId(v));
  const busy = !!restoringId || !!exportingId;

  const row = (v, { wholeTemplate = false } = {}) => {
    const restoring = restoringId === v.id;
    const exporting = exportingId === v.id;
    const counts = countContent(v.sections);
    // The number the EXPORTED document carries for this snapshot. Only
    // approvals are numbered: a built or backup snapshot is a safety net, not
    // a version of the document anyone signed, and numbering it would put a
    // "Version 3.0" on this list that no printed source document has.
    const label = versionLabel(approvalNumberOf(all, v));
    return (
      <li
        key={v.id}
        className="rounded-xl border border-white/10 bg-white/[0.03] p-3 space-y-2"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className={`inline-flex items-center px-1.5 py-0.5 rounded-md border text-[10px] font-semibold uppercase tracking-wide ${
                  KIND_COLOR[v.kind] || KIND_COLOR.backup
                }`}
              >
                {KIND_LABEL[v.kind] || v.kind}
              </span>
              {label && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-md border border-purple-300/30 bg-purple-400/15 text-[10px] font-semibold uppercase tracking-wide text-purple-100">
                  {label}
                </span>
              )}
              <span className="text-sm text-slate-100 font-medium">
                {formatWhen(v.createdAt)}
              </span>
            </div>
            {/* The visit is named on every row, not just in the title — a
                version outlives the visit it came from, and a row that only
                says "Approved, 24 Aug" is unusable once it does. */}
            <p className="mt-0.5 text-[11px] text-slate-400">
              {wholeTemplate ? (
                <>
                  {counts.sections} section{counts.sections === 1 ? "" : "s"} ·{" "}
                  {counts.events} visit{counts.events === 1 ? "" : "s"} ·{" "}
                  {counts.questions} field{counts.questions === 1 ? "" : "s"}
                </>
              ) : (
                <>
                  <span className="text-slate-300">
                    {v.eventName || eventName || "this visit"}
                  </span>{" "}
                  · {countVisit(v.sections)} field
                  {countVisit(v.sections) === 1 ? "" : "s"}
                </>
              )}
            </p>
            {/* WHO did the thing this row records, and when — named as the
                thing it was.
                  - approved: somebody put their name to this. It is the one row
                    on the list that is a sign-off, and "by Jane Doe" in a
                    backup's grey did not say so.
                  - built:    somebody re-ran the Protocol → eCRF step and
                    replaced everything. "Rebuilt by" is the answer to "who
                    overwrote the documents, and when?".
                An older entry, written before either was stamped, has no actor
                and says what it is rather than leaving a blank where a name
                should be. */}
            {v.actor ? (
              <p
                className={`mt-0.5 text-[11px] ${
                  v.kind === "approved"
                    ? "text-emerald-300/90"
                    : v.kind === "built"
                    ? "text-rose-200/80"
                    : "text-slate-500"
                }`}
              >
                {v.kind === "approved"
                  ? "Approved by "
                  : v.kind === "built"
                  ? "Rebuilt by "
                  : "by "}
                {describeActor(v.actor)}
                {(v.kind === "approved" || v.kind === "built") && (
                  <span className="opacity-70">
                    {" · "}
                    {formatWhen(v.createdAt)}
                  </span>
                )}
              </p>
            ) : (
              v.kind === "built" && (
                <p className="mt-0.5 text-[11px] text-slate-500">
                  Rebuilt {formatWhen(v.createdAt)} — before this step recorded who
                </p>
              )
            )}
            {v.label && (
              <p className="mt-0.5 text-xs text-slate-400 truncate">{v.label}</p>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          {/* Both are optional. A caller that browses this history without an
              exporter or a redliner — the mapping page does — used to get the
              buttons anyway, and clicking one called `undefined(v)`. Drawing a
              control for something the caller cannot do is worse than not
              drawing it. */}
          {/* Export first and filled in: reading an old version is the common
              case, and it changes nothing. */}
          {onExport && (
            <button
              onClick={() => onExport(v)}
              disabled={busy}
              title="Download this version as a Word document"
              className={`${actionBtn} text-emerald-100 bg-emerald-400/15 border border-emerald-400/35 hover:bg-emerald-400/25`}
            >
              {exporting ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <FileDown className="w-3.5 h-3.5" />
              )}
              {exporting ? "Building…" : "Export to Word"}
            </button>
          )}
          {onRedline && !wholeTemplate && (
            <button
              onClick={() => onRedline(v)}
              disabled={busy}
              title="See what changed between the previous approved version and this one"
              className={`${actionBtn} text-sky-100 bg-sky-400/10 border border-sky-400/30 hover:bg-sky-400/20`}
            >
              <GitCompare className="w-3.5 h-3.5" />
              What changed
            </button>
          )}
          <button
            onClick={() => onRestore(v)}
            disabled={busy}
            title={
              wholeTemplate
                ? "Replace EVERY visit with this snapshot"
                : "Replace this visit's content with this version"
            }
            className={`${actionBtn} ml-auto text-slate-300 bg-white/[0.05] border border-white/10 hover:bg-white/[0.12]`}
          >
            {restoring ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <ArchiveRestore className="w-3.5 h-3.5" />
            )}
            Restore
          </button>
        </div>
      </li>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-xl rounded-2xl border border-white/10 bg-[#0c0726] p-5 shadow-2xl">
        <div className="mb-1 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-100 inline-flex items-center gap-2">
            <History size={18} /> Version History
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-200">
            <X size={18} />
          </button>
        </div>
        <p className="mb-4 text-xs text-slate-500">
          {eventName ? (
            <>
              Versions of <span className="text-slate-300">{eventName}</span>. One is
              saved automatically whenever this visit is rebuilt, approved or
              restored, so earlier work is never just gone.
            </>
          ) : (
            // No visit selected — the mapping page. What it is looking at is
            // the set of built documents as a whole, saved each time step 3
            // replaced it.
            "Every rebuild of the source documents, newest first — the set as it stood before each one replaced it."
          )}
        </p>

        {loading ? (
          <div className="py-8 flex items-center justify-center gap-2 text-sm text-slate-400">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : mine.length === 0 && legacy.length === 0 ? (
          <p className="py-8 text-center text-sm text-slate-500">
            No earlier versions yet — one will appear here the next time this visit
            is rebuilt, approved or restored.
          </p>
        ) : (
          <div className="max-h-[26rem] overflow-y-auto scrollbar-thin space-y-3 pr-1">
            {mine.length > 0 && <ul className="space-y-2">{mine.map((v) => row(v))}</ul>}

            {mine.length === 0 && legacy.length > 0 && eventId && (
              <p className="text-sm text-slate-500">
                No versions of this visit yet.
              </p>
            )}

            {/* Un-scoped snapshots. What they ARE depends on who is asking: in
                the editor they are the old pre-per-visit ones, and calling them
                that is the warning. On the mapping page, which selects no
                visit, they are the step-3 assemblies and there is nothing
                legacy about them — but "restoring one replaces every visit"
                remains true and is exactly what somebody there needs told. */}
            {legacy.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-start gap-1.5 pt-1 border-t border-white/10">
                  <AlertTriangle className="w-3.5 h-3.5 mt-2 shrink-0 text-amber-300" />
                  <div className="pt-1.5">
                    <div className="text-[11px] uppercase tracking-wide text-amber-200">
                      {eventId ? "Whole-template snapshots" : "Every visit at once"}
                    </div>
                    <p className="text-[11px] text-slate-500">
                      {eventId
                        ? "Taken before history was kept per visit. Restoring one replaces every visit, not just this one."
                        : "Each one is the whole set of built documents. Restoring replaces every visit."}
                    </p>
                  </div>
                </div>
                <ul className="space-y-2">
                  {legacy.map((v) => row(v, { wholeTemplate: true }))}
                </ul>
              </div>
            )}
          </div>
        )}

        <div className="mt-4 flex justify-end">
          <button
            onClick={onClose}
            className="rounded-xl border border-white/15 px-4 py-2 text-sm text-slate-300 hover:bg-white/5"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
